import * as React from 'react';
import dynamic from 'next/dynamic';
import { getEmailOffer } from 'constants/emailOffers';
import { AuthStatus } from 'constants/auth';
import { MEMBER_OFFERS_API, OFFER_CARD_API, PRICING_PAGE } from 'constants/pages';
import { SaleTerm } from 'constants/payments';
import type {
  MemberOffersResponse,
  MemberOfferSubscription,
  OfferCardResult,
} from 'constants/payloads/memberOffers';
import useViewer from 'hooks/useViewer';
import { hasDashboardSubscription } from 'utils/client/dashboardPlan';
import api from 'services/client/api';
import { offerFailureCode, offerRead } from 'services/client/offerRead';
import { trackEvent } from 'services/client/analytics';
import { MEMBER_OFFER_OPENED, MEMBER_SALE_DISMISSED } from 'services/client/analytics/events';
import { getIsNativePlatform } from 'utils/client/mobile';
import type { MemberOfferEntry } from 'components/MemberOffers/MemberOffersModal';

const MemberOffersModal = dynamic(() => import('components/MemberOffers/MemberOffersModal'), {
  ssr: false,
});
const CACHE_MS = 5 * 60 * 1000;
/**
 * Every open tab refreshes when a sale starts or ends. Spreading those refreshes over two minutes
 * keeps Black Friday's first second from becoming one Stripe burst; a sale that ends hides itself
 * on its own clock first, so nothing trails it while the refresh waits.
 */
const BOUNDARY_JITTER_MS = 2 * 60 * 1000;
const jitter = () => Math.floor(Math.random() * BOUNDARY_JITTER_MS);
const DAY_MS = 24 * 60 * 60 * 1000;
const clearReturnParam = (param: 'memberCard' | 'memberOfferResume') => {
  const clean = new URL(window.location.href);
  if (!clean.searchParams.has(param)) return;
  clean.searchParams.delete(param);
  window.history.replaceState(window.history.state, '', clean.pathname + clean.search + clean.hash);
};
const clearCardReturnUrl = () => clearReturnParam('memberCard');
/** A member who closed the card page comes back to the offer they were on: `<plan id>.<term>`. */
const RESUME =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.(ANNUAL|LIFETIME)$/i;
const storageKey = (uid: string, campaign: string, state: string) =>
  `if:member-offers:${uid}:${campaign}:${state}`;
/** The campaign a sale banner announces: an annual sale first, else a Lifetime sale. */
const campaignOf = (data: MemberOffersResponse | null) => {
  const annual = data?.subscriptions.find((row) => row.sale)?.sale;
  if (annual) return { id: annual.id, title: annual.title, endsAt: annual.endsAt };
  const lifetime = data?.subscriptions.find((row) => row.lifetime?.sale)?.lifetime?.sale;
  return lifetime ? { id: lifetime.id, title: lifetime.title, endsAt: lifetime.endsAt } : null;
};
const offerIdentity = (data: MemberOffersResponse) =>
  campaignOf(data)?.id ||
  (data.subscriptions.length
    ? `evergreen:${data.subscriptions
        .map((row) => `${row.id}:${row.currentTerm}:${row.terms.join(',')}`)
        .sort()
        .join(';')}`
    : null);
const read = (key: string) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const write = (key: string) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* In-memory state still applies. */
  }
};

interface Context {
  subscriptions: MemberOfferSubscription[];
  loading: boolean;
  discoveryError: boolean;
  isOpen: boolean;
  unseen: boolean;
  showBanner: boolean;
  sale: MemberOfferSubscription['sale'];
  /** The running campaign the banner announces, annual or Lifetime; null once it ends. */
  campaign: { id: string; title: string; endsAt: number } | null;
  clockOffset: number;
  /** `term` opens that plan tab, so a Lifetime sale's button lands on Lifetime. */
  open: (
    subscriptionId?: string,
    offerCode?: string,
    entry?: MemberOfferEntry,
    term?: OfferCardResult['term'],
  ) => void;
  /** Manual retries bypass failure cooldown; background boundary/focus refreshes do not. */
  refresh: (force?: boolean, manualRetry?: boolean) => Promise<void>;
  dismissBanner: () => void;
}

const empty: Context = {
  subscriptions: [],
  loading: false,
  discoveryError: false,
  isOpen: false,
  unseen: false,
  showBanner: false,
  sale: null,
  campaign: null,
  clockOffset: 0,
  open: () => {},
  refresh: async () => {},
  dismissBanner: () => {},
};
const MemberOffersContext = React.createContext<Context>(empty);
export const useMemberOffers = () => React.useContext(MemberOffersContext);

export function MemberOffersProvider({ children }: React.PropsWithChildren<{}>) {
  const viewer = useViewer();
  const uid =
    viewer.status === AuthStatus.User && !getIsNativePlatform() ? viewer.viewer?.uid : undefined;
  // Only a paid Dashboard plan has an offer to keep fresh. The claim lists active and trialing plans
  // only, so a free, Technical Analysis-only or past-due member has none: their page loads read
  // nothing, the modal opens on the plans, and one read on open finds any step the account needs
  // first, such as an overdue bill.
  const canHaveOffers = !!uid && hasDashboardSubscription(viewer);
  const account = React.useRef({ uid, version: 0 });
  if (account.current.uid !== uid) account.current = { uid, version: account.current.version + 1 };
  const [snapshot, setSnapshot] = React.useState<{
    uid: string;
    version: number;
    data: MemberOffersResponse;
  } | null>(null);
  const data =
    snapshot && snapshot.uid === uid && snapshot.version === account.current.version
      ? snapshot.data
      : null;
  const [discoveryError, setDiscoveryError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isOpen, setOpen] = React.useState(false);
  const [initialId, setInitialId] = React.useState<string>();
  const [initialTerm, setInitialTerm] = React.useState<OfferCardResult['term']>();
  const [offerCode, setOfferCode] = React.useState<string>();
  const [cardResult, setCardResult] = React.useState<OfferCardResult>();
  const [cardPending, setCardPending] = React.useState(false);
  const [cardError, setCardError] = React.useState(false);
  const cardReturn = React.useRef<{
    uid: string;
    sessionId: string;
    retry: number;
    promise: Promise<OfferCardResult>;
  }>();
  const [cardRetry, setCardRetry] = React.useState(0);
  const cardGeneration = React.useRef(0);
  const completedRecovery = React.useRef<string>();
  const [entry, setEntry] = React.useState<MemberOfferEntry>('nav');
  const [endedCampaign, setEndedCampaign] = React.useState<string | null>(null);
  const [unseen, setUnseen] = React.useState(false);
  const [showBanner, setShowBanner] = React.useState(false);
  const [clockOffset, setClockOffset] = React.useState(0);
  const currentUid = React.useRef(uid);
  currentUid.current = uid;
  const request = React.useRef<{
    uid: string;
    version: number;
    promise: Promise<void>;
    controller: AbortController;
  } | null>(null);
  // `withPlan` is the claim the read was made under: a plan bought mid-session reads at once.
  const loadedAt = React.useRef({ uid, version: 0, time: 0, withPlan: false });
  const failedAt = React.useRef({ uid, version: 0, time: 0, withPlan: false });
  const openOwner = React.useRef({ uid, version: 0 });
  const visibleOpen =
    isOpen &&
    openOwner.current.uid === uid &&
    openOwner.current.version === account.current.version;
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const close = React.useCallback(() => {
    cardGeneration.current += 1;
    clearCardReturnUrl();
    setCardPending(false);
    setCardError(false);
    setOpen(false);
    request.current?.controller.abort();
    window.setTimeout(() => {
      if (returnFocus.current?.isConnected) returnFocus.current.focus();
    }, 0);
  }, []);
  const shownCampaign = React.useRef<string | null>(null);
  const resetUid = React.useRef(uid);
  React.useEffect(() => {
    if (resetUid.current === uid) return;
    resetUid.current = uid;
    // A child's effect may already have started discovery for this account. Abort only
    // a request owned by the previous generation, never that newly started read.
    if (
      request.current &&
      (request.current.uid !== uid || request.current.version !== account.current.version)
    )
      request.current.controller.abort();
    setOpen(false);
    setOfferCode(undefined);
    setCardResult(undefined);
    setCardPending(false);
    cardReturn.current = undefined;
    setShowBanner(false);
    setUnseen(false);
    setDiscoveryError(false);
    shownCampaign.current = null;
  }, [uid]);

  const refresh = React.useCallback(
    async (force = false, manualRetry = false) => {
      if (!uid || currentUid.current !== uid) return;
      // Without a paid Dashboard plan claim there is no offer to keep fresh: a page load or focus
      // reads nothing, and only opening the modal asks (`open` forces the read).
      if (!canHaveOffers && !force) return;
      const version = account.current.version;
      if (
        request.current?.uid === uid &&
        request.current.version === version &&
        !request.current.controller.signal.aborted
      )
        return request.current.promise;
      // A failed read already exhausted its bounded network retries. Focus and sale-boundary
      // events must not restart that sequence on every visit; the visible Reload action can.
      if (
        !manualRetry &&
        failedAt.current.uid === uid &&
        failedAt.current.version === version &&
        failedAt.current.withPlan === canHaveOffers &&
        Date.now() - failedAt.current.time < CACHE_MS
      )
        return;
      if (
        !force &&
        loadedAt.current.uid === uid &&
        loadedAt.current.version === version &&
        loadedAt.current.withPlan === canHaveOffers &&
        Date.now() - loadedAt.current.time < CACHE_MS
      )
        return;
      request.current?.controller.abort();
      const capturedUid = uid;
      const controller = new AbortController();
      setLoading(true);
      const pending = (async () => {
        try {
          const response: MemberOffersResponse = await offerRead<MemberOffersResponse>(
            MEMBER_OFFERS_API,
            {
              signal: controller.signal,
              current: () =>
                currentUid.current === capturedUid && account.current.version === version,
              timeoutMs: 20000,
            },
          );
          if (
            controller.signal.aborted ||
            currentUid.current !== capturedUid ||
            account.current.version !== version
          )
            return;
          loadedAt.current = {
            uid: capturedUid,
            version,
            time: Date.now(),
            withPlan: canHaveOffers,
          };
          failedAt.current = { uid: capturedUid, version, time: 0, withPlan: canHaveOffers };
          setDiscoveryError(false);
          setSnapshot({ uid: capturedUid, version, data: response });
          setClockOffset(response.serverNow - Date.now());
          const sale = campaignOf(response);
          const identity = offerIdentity(response);
          setUnseen(!!identity && !read(storageKey(capturedUid, identity, 'viewed')));
          // The banner stays until the member closes it or turns the sale down. Opening the offer
          // hides it for this visit only.
          if (sale && shownCampaign.current !== sale.id) {
            shownCampaign.current = sale.id;
            setShowBanner(!read(storageKey(capturedUid, sale.id, 'dismissed')));
          } else if (!sale) setShowBanner(false);
        } catch {
          // Promotion outages never block the app. Retry on a later focus/modal open.
          if (
            !controller.signal.aborted &&
            currentUid.current === capturedUid &&
            account.current.version === version
          ) {
            failedAt.current = {
              uid: capturedUid,
              version,
              time: Date.now(),
              withPlan: canHaveOffers,
            };
            setSnapshot(null);
            setDiscoveryError(true);
            setShowBanner(false);
            setUnseen(false);
          }
        } finally {
          if (
            request.current?.controller === controller &&
            currentUid.current === capturedUid &&
            account.current.version === version
          ) {
            setLoading(false);
            request.current = null;
          }
        }
      })();
      request.current = { uid, version, promise: pending, controller };
      return pending;
    },
    [uid, canHaveOffers],
  );

  React.useEffect(() => {
    if (!uid) return;
    const sessionId = new URLSearchParams(window.location.search).get('memberCard');
    if (!sessionId || !/^cs_[a-zA-Z0-9_]{1,200}$/.test(sessionId)) return;
    const cached = cardReturn.current;
    const promise: Promise<OfferCardResult> =
      cached?.uid === uid && cached.sessionId === sessionId && cached.retry === cardRetry
        ? cached.promise
        : api.post(OFFER_CARD_API, { payload: { action: 'finish', sessionId }, timeoutMs: 30000 });
    cardReturn.current = { uid, sessionId, retry: cardRetry, promise };
    const version = account.current.version;
    const generation = ++cardGeneration.current;
    let current = true;
    const isCurrent = () =>
      current &&
      currentUid.current === uid &&
      account.current.version === version &&
      cardGeneration.current === generation;
    setCardPending(true);
    setCardError(false);
    openOwner.current = { uid, version };
    setOpen(true);
    void promise
      .then((result: OfferCardResult) => {
        if (!isCurrent()) return;
        setCardResult(result);
        setOfferCode(result.offerCode);
        setInitialId(result.subscriptionId);
        setEntry(result.offerCode ? 'email' : 'card');
        // Keep the opaque return reference until the member closes or completes the offer.
        // A browser reload must restore the selected card and term, including an older saved card.
        void refresh(true);
      })
      .catch((error) => {
        if (!isCurrent()) return;
        if (offerFailureCode(error) === 'CARD_REFRESH') {
          clearCardReturnUrl();
          setCardResult(undefined);
          setOfferCode(undefined);
          setInitialId(undefined);
          void refresh(true);
        } else setCardError(true);
      })
      .finally(() => {
        if (isCurrent()) setCardPending(false);
      });
    return () => {
      current = false;
    };
  }, [uid, cardRetry, refresh]);

  React.useEffect(() => {
    if (
      !cardResult?.subscriptionId ||
      completedRecovery.current === cardResult.sessionId ||
      !data ||
      loading
    )
      return;
    if (
      data.subscriptions.some(
        (row) =>
          row.id === cardResult.subscriptionId &&
          row.terms.some((term) => term === cardResult.term),
      )
    )
      return;
    // Another tab may have completed the Annual upgrade while card setup was open.
    // Return to the current eligible offers instead of retaining an unsupported recovery target.
    clearCardReturnUrl();
    setCardResult(undefined);
    setOfferCode(undefined);
  }, [cardResult, data, loading]);
  const recoverySubscriptions = cardResult?.subscriptionId
    ? data?.subscriptions.filter(
        (row) =>
          row.id === cardResult.subscriptionId &&
          row.terms.some((term) => term === cardResult.term),
      ) || []
    : data?.subscriptions || [];
  const annualSale = data?.subscriptions.find((row) => row.sale)?.sale || null;
  const rawCampaign = campaignOf(data);
  // A campaign stops showing the instant it ends, on the server's clock, before any refresh.
  const campaign = rawCampaign && rawCampaign.id !== endedCampaign ? rawCampaign : null;
  const sale = annualSale && campaign?.id === annualSale.id ? annualSale : null;
  const [endCheck, setEndCheck] = React.useState(0);
  React.useEffect(() => {
    if (!rawCampaign || rawCampaign.id === endedCampaign) return;
    const remaining = rawCampaign.endsAt - Date.now() - clockOffset;
    // A browser timer cannot wait weeks; a far deadline is re-checked daily instead of ended.
    if (remaining > DAY_MS) {
      const recheck = window.setTimeout(() => setEndCheck((value) => value + 1), DAY_MS);
      return () => window.clearTimeout(recheck);
    }
    const timer = window.setTimeout(() => {
      setEndedCampaign(rawCampaign.id);
      setShowBanner(false);
      window.setTimeout(() => void refresh(true), jitter());
    }, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [rawCampaign?.id, rawCampaign?.endsAt, endedCampaign, clockOffset, refresh, endCheck]);
  React.useEffect(() => {
    const identity = data && offerIdentity(data);
    if (uid && visibleOpen && identity) {
      setShowBanner(false);
      write(storageKey(uid, identity, 'viewed'));
      setUnseen(false);
    }
  }, [uid, visibleOpen, data]);
  React.useEffect(() => {
    // Sale boundaries matter only to a member with offers to refresh.
    if (!uid || !canHaveOffers || !data?.nextCheckAt) return;
    const delay =
      Math.max(
        100,
        Math.min(24 * 60 * 60 * 1000, data.nextCheckAt - Date.now() - clockOffset + 100),
      ) + jitter();
    const timer = window.setTimeout(() => void refresh(true), delay);
    return () => window.clearTimeout(timer);
  }, [uid, canHaveOffers, data?.nextCheckAt, clockOffset, refresh]);
  React.useEffect(() => {
    if (!uid) return;
    const changed = (event: StorageEvent) => {
      if (campaign && event.key === storageKey(uid, campaign.id, 'dismissed')) setShowBanner(false);
      const identity = data && offerIdentity(data);
      if (identity && event.key === storageKey(uid, identity, 'viewed')) setUnseen(false);
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [uid, campaign, data]);
  const open = React.useCallback(
    (
      subscriptionId?: string,
      code?: string,
      from: MemberOfferEntry = 'nav',
      term?: OfferCardResult['term'],
    ) => {
      if (!uid) return;
      cardGeneration.current += 1;
      clearCardReturnUrl();
      setCardPending(false);
      setCardError(false);
      openOwner.current = { uid, version: account.current.version };
      returnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOfferCode(code);
      setCardResult(undefined);
      setEntry(from);
      setInitialId(
        subscriptionId || data?.subscriptions.find((row) => row.sale || row.lifetime?.sale)?.id,
      );
      setInitialTerm(term);
      setOpen(true);
      setShowBanner(false);
      const identity = data && offerIdentity(data);
      if (identity) {
        write(storageKey(uid, identity, 'viewed'));
        setUnseen(false);
      }
      trackEvent(MEMBER_OFFER_OPENED, {
        campaign: getEmailOffer(code || '')?.id || campaign?.id || 'member-evergreen',
        entry: from,
        // Someone with no Dashboard plan opens the plans, not an offer.
        view: canHaveOffers || code ? 'offers' : 'pricing',
      });
      void refresh(true, true);
    },
    [uid, campaign, data, refresh, canHaveOffers],
  );
  // Closing the card page returns here with the offer named in the address: open it again.
  React.useEffect(() => {
    if (!uid) return;
    const resume = new URLSearchParams(window.location.search).get('memberOfferResume');
    if (!resume) return;
    clearReturnParam('memberOfferResume');
    const match = RESUME.exec(resume);
    if (match)
      open(
        match[1],
        undefined,
        'card',
        match[2].toUpperCase() === SaleTerm.Lifetime ? SaleTerm.Lifetime : SaleTerm.Annual,
      );
  }, [uid, open]);
  // The banner goes for good when the member closes it, or turns the sale offer down. Only the
  // close is a banner dismissal; a decline is already recorded as `member_offer_declined`.
  const hideBannerForGood = React.useCallback(
    (fromBanner: boolean) => {
      setShowBanner(false);
      if (uid && campaign) {
        write(storageKey(uid, campaign.id, 'dismissed'));
        if (fromBanner) trackEvent(MEMBER_SALE_DISMISSED, { campaign: campaign.id });
      }
    },
    [uid, campaign],
  );
  const dismissBanner = React.useCallback(() => hideBannerForGood(true), [hideBannerForGood]);
  const declineSale = React.useCallback(() => hideBannerForGood(false), [hideBannerForGood]);
  const onComplete = React.useCallback(() => {
    completedRecovery.current = cardResult?.sessionId;
    clearCardReturnUrl();
    void refresh(true, true);
    window.dispatchEvent(new Event('if:member-offer-completed'));
  }, [refresh, cardResult?.sessionId]);
  return (
    <MemberOffersContext.Provider
      value={{
        subscriptions: uid ? data?.subscriptions || [] : [],
        loading,
        discoveryError,
        isOpen: visibleOpen,
        unseen,
        showBanner: showBanner && !!campaign,
        sale,
        campaign,
        clockOffset,
        open,
        refresh,
        dismissBanner,
      }}
    >
      {children}
      {uid && visibleOpen && (
        <MemberOffersModal
          key={cardResult?.sessionId || 'member-offers'}
          offerCode={offerCode}
          initialTerm={cardResult?.term || initialTerm}
          cardSessionId={cardResult?.sessionId}
          subscriptions={cardError || cardPending ? [] : recoverySubscriptions}
          // Someone without a paid Dashboard plan sees the plans at once. The read on open can only
          // replace them with a next step the account needs first, such as an overdue bill.
          loading={(canHaveOffers && loading) || cardPending}
          discoveryError={(canHaveOffers && discoveryError) || cardError}
          initialSubscriptionId={initialId}
          clockOffset={clockOffset}
          entry={entry}
          onDeclineSale={declineSale}
          emptyReason={data?.emptyReason}
          nextAction={
            data?.nextAction ||
            (canHaveOffers ? undefined : { kind: 'pricing', href: PRICING_PAGE })
          }
          onClose={close}
          onComplete={onComplete}
          onReload={() => {
            if (cardError) setCardRetry((value) => value + 1);
            else void refresh(true, true);
          }}
        />
      )}
    </MemberOffersContext.Provider>
  );
}
